<template>
  <aside class="insight-panel" aria-label="小奇图表解读">
    <header class="insight-panel__header">
      <div class="insight-panel__brand">
        <img class="agent-avatar" src="@/assets/images/xiaoqi-avatar.png" alt="小奇智能体" />
        <span class="brand-copy">
          <strong>小奇分析</strong>
          <small :class="`state-${aiState}`">{{ stateText }}</small>
        </span>
      </div>
      <div class="insight-panel__actions">
        <v-tooltip :text="pinned ? '取消固定' : '固定面板'" location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn v-bind="tooltipProps" :icon="pinned ? 'mdi-pin' : 'mdi-pin-outline'" variant="text"
              density="comfortable" :color="pinned ? 'primary' : undefined" :aria-pressed="pinned"
              :aria-label="pinned ? '取消固定分析面板' : '固定分析面板'" @click="$emit('toggle-pin')" />
          </template>
        </v-tooltip>
        <v-tooltip text="重新分析" location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn v-bind="tooltipProps" icon="mdi-refresh" variant="text" density="comfortable" aria-label="重新分析"
              :loading="aiState === 'loading'" @click="$emit('retry')" />
          </template>
        </v-tooltip>
        <v-tooltip :text="pinned ? '取消固定后可关闭' : '关闭'" location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn v-bind="tooltipProps" icon="mdi-close" variant="text" density="comfortable" aria-label="关闭分析面板"
              :disabled="pinned" @click="$emit('close')" />
          </template>
        </v-tooltip>
      </div>
    </header>

    <div class="insight-panel__scope">
      <v-icon size="16">mdi-database-search-outline</v-icon>
      <span>{{ scopeLabel }}</span>
    </div>

    <div ref="bodyRef" class="insight-panel__body">
      <div v-if="aiState === 'loading'" class="panel-loading" role="status">
        <div class="panel-loading__lead">
          <v-progress-circular indeterminate color="primary" size="22" width="2" />
          <span>
            <strong>正在解读图表</strong>
            <small>正在读取各图表的变化和分布</small>
          </span>
        </div>
        <div v-for="index in 3" :key="index" class="finding-skeleton">
          <v-skeleton-loader type="heading, paragraph, paragraph" />
        </div>
      </div>

      <div v-else-if="aiState === 'error'" class="panel-state">
        <span class="panel-state__icon is-error"><v-icon size="24">mdi-alert-circle-outline</v-icon></span>
        <strong>分析未完成</strong>
        <p>{{ errorMessage || '当前数据分析遇到问题，请稍后重试。' }}</p>
        <v-btn color="primary" variant="tonal" size="small" prepend-icon="mdi-refresh" @click="$emit('retry')">
          重新分析
        </v-btn>
      </div>

      <template v-else-if="aiState === 'done' && insights.length">
        <div class="insight-list">
          <article v-for="(insight, index) in insights" :key="insight.cardId || insight.chartKey" class="insight-item">
            <button type="button" class="insight-item__chart" :title="`定位到${insight.chartTitle}图表`"
              :aria-label="`定位到${insight.chartTitle}图表`" @click="$emit('focus-chart', insight.chartKey)">
              <span>{{ String(insight.order || index + 1).padStart(2, '0') }}</span>
              <strong>{{ insight.chartTitle }}</strong>
              <v-icon size="15">mdi-crosshairs-gps</v-icon>
            </button>
            <!-- <h3>{{ insight.headline || (insight.status === 'insufficient_data' ? '证据不足' : '暂无结论') }}</h3> -->
            <p v-if="insight.status === 'insufficient_data'">
              {{ insight.missingEvidence?.join('；') || '当前数据不足以形成可靠结论。' }}
            </p>
            <div v-else class="insight-item__body">
              <template v-for="(block, blockIndex) in parseInsightBody(insight.body)" :key="blockIndex">
                <ul v-if="block.type === 'list'">
                  <li v-for="(item, itemIndex) in block.items" :key="itemIndex">{{ item }}</li>
                </ul>
                <p v-else>{{ block.text }}</p>
              </template>
            </div>
            <!-- <div v-if="insight.status === 'ready' && insight.evidenceRefs?.length" class="insight-item__evidence">
              <span>依据：</span>
              <button type="button" @click="$emit('focus-chart', insight.chartKey)">
                {{ insight.evidenceRefs.join('、') }}
              </button>
            </div> -->
            <button v-if="insight.status === 'ready' && insight.action?.type === 'navigate_station'" type="button"
              class="insight-item__action" @click="$emit('go-station', insight.action.stationId)">
              <span>{{ insight.action.label || `查看 ${insight.action.stationName}` }}</span>
              <v-icon size="15">mdi-arrow-right</v-icon>
            </button>
          </article>
        </div>
      </template>

      <div v-else class="panel-state">
        <span class="panel-state__icon"><v-icon size="24">mdi-check-circle-outline</v-icon></span>
        <strong>暂无图表解读</strong>
        <p>当前范围内暂时没有可解读的数据。</p>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  aiState: { type: String, default: 'idle' },
  insights: { type: Array, default: () => [] },
  scopeLabel: { type: String, default: '' },
  errorMessage: { type: String, default: '' },
  pinned: { type: Boolean, default: false },
})

defineEmits(['close', 'retry', 'go-station', 'focus-chart', 'toggle-pin'])

const bodyRef = ref(null)

function scrollToTop() {
  bodyRef.value?.scrollTo({ top: 0, behavior: 'auto' })
}

defineExpose({ scrollToTop })

const stateText = computed(() => ({
  idle: '等待分析',
  loading: '分析中',
  done: '解读完成',
  error: '分析失败',
}[props.aiState] || '等待分析'))

function normalizeInsightBody(body) {
  return String(body ?? '')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
}

function parseInsightBody(body) {
  const blocks = []
  let textLines = []
  let listItems = []

  const flushText = () => {
    const text = textLines.join('\n').trim()
    if (text) blocks.push({ type: 'text', text })
    textLines = []
  }
  const flushList = () => {
    if (listItems.length) blocks.push({ type: 'list', items: listItems })
    listItems = []
  }

  normalizeInsightBody(body).split('\n').forEach(line => {
    const listItem = line.match(/^\s*-\s+(.+)$/)
    if (listItem) {
      flushText()
      listItems.push(listItem[1].trim())
      return
    }
    flushList()
    textLines.push(line)
  })

  flushText()
  flushList()
  return blocks
}
</script>

<style scoped lang="scss">
.insight-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  color: var(--text-primary);
  background: #fff;
  border: 0;
  border-radius: 10px;
  box-shadow: var(--shadow-card);
}

.insight-panel__header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  min-height: 66px;
  padding: 10px 12px 10px 16px;
  // border-bottom: 1px solid var(--border);
}

.insight-panel__brand,
.brand-copy,
.insight-panel__actions,
.panel-loading__lead {
  display: flex;
  align-items: center;
}

.panel-loading__lead {
  margin-top: 20px;
}

.insight-panel__brand {
  gap: 10px;
}

.agent-avatar {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  object-fit: contain;
  transform: scale(1.4);
}

.brand-copy {
  align-items: flex-start;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 14px;
    font-weight: 600;
  }

  small {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--text-muted);
    font-size: 11px;

    &::before {
      width: 5px;
      height: 5px;
      background: currentColor;
      border-radius: 50%;
      content: '';
    }
  }

  .state-loading {
    color: var(--primary);
  }

  .state-done {
    color: var(--color-success, #12B76A);
  }

  .state-error {
    color: var(--cat-safety);
  }
}

.insight-panel__actions {
  gap: 2px;
}

.insight-panel__scope {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 8px;
  padding: 0 18px 10px;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.5;
  background: #fff;
  border-bottom: 1px solid var(--border);

  .v-icon {
    flex: 0 0 auto;
    margin-top: 1px;
    color: var(--primary);
  }
}

.insight-panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 16px 16px 24px;
}

.panel-loading__lead {
  gap: 10px;
  margin-bottom: 16px;

  >span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    font-size: 13px;
    font-weight: 600;
  }

  small {
    color: var(--text-muted);
    font-size: 11px;
  }
}

.finding-skeleton {
  margin-bottom: 10px;
  overflow: hidden;
  background: var(--surface-muted);
  border-radius: 8px;

  :deep(.v-skeleton-loader__bone) {
    margin: 10px 12px;
  }
}

.insight-list {
  display: flex;
  flex-direction: column;
}

.insight-item {
  padding: 16px 0 18px;
  border-bottom: 1px solid var(--border);

  h3 {
    margin: 12px 0 6px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
  }

  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 13px;
    line-height: 1.7;
    white-space: pre-line;
  }

  &:last-child {
    border-bottom: 0;
  }
}

.insight-item__body {
  display: flex;
  flex-direction: column;
  gap: 6px;

  ul {
    margin: 0;
    padding-left: 18px;
    color: var(--text-secondary);
    font-size: 13px;
    line-height: 1.7;
    list-style: disc;
  }

  li {
    padding-left: 2px;

    &::marker {
      color: var(--primary);
    }
  }
}

.insight-item__chart {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0 0 10px 0;
  color: var(--text-primary);
  font: 12px;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;

  >span {
    color: var(--primary);
    font-size: 10px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  strong {
    flex: 1;
    font-size: 12px;
    font-weight: 600;
  }

  .v-icon {
    color: var(--text-muted);
    transition: color 0.2s, transform 0.2s;
  }

  &:hover .v-icon {
    color: var(--primary);
    transform: scale(1.08);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 4px;
  }
}

.insight-item__evidence {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 8px;
  color: var(--text-muted);
  font-size: 11px;

  >span {
    flex: 0 0 auto;
  }

  button {
    min-width: 0;
    padding: 0;
    overflow: hidden;
    color: var(--primary);
    font: inherit;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: transparent;
    border: 0;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 2px;
    }
  }
}

.insight-item__action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: auto;
  margin-top: 10px;
  padding: 0;
  overflow: hidden;
  color: var(--primary);
  font: inherit;
  font-size: 11px;
  font-weight: 500;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 0;
  cursor: pointer;
  transition: color 0.2s, transform 0.2s;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    color: color-mix(in srgb, var(--primary) 78%, #000);
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
}

.panel-state {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 24px;
  text-align: center;

  &__icon {
    margin-bottom: 10px;
    color: var(--color-success, #12B76A);

    &.is-error {
      color: var(--cat-safety);
    }
  }

  strong {
    font-size: 14px;
    font-weight: 600;
  }

  p {
    margin: 6px 0 16px;
    color: var(--text-muted);
    font-size: 12px;
    line-height: 1.6;
  }
}
</style>
